import { useSyncContract } from '@actions';
import { ReactComponent as SettingsIcon } from '@assets/icons/V4/settings-icon.svg';
import Button from '@components/V4/Button';
import IconButton from '@components/V4/IconButton';
import { PaginationProps } from '@components/V4/Pagination';
import Table, { SortOrder } from '@components/V4/Table/Table';
import { RefreshIcon } from '@heroicons/react/outline';
import { ContractRowDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import { CONTRACT_CONTACT_FORM_LABELS, CONTRACT_STATUS_LABELS } from '@shared/constants';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useState } from 'react';
import { churnTone, contractStatusTone, formatDate, formatMoney } from '../utils';

interface ContractsTableProps {
  contracts?: ContractRowDTO[];
  isLoading: boolean;
  pagination: PaginationProps;
  sortBy: string | null;
  sortOrder: SortOrder | null;
  setSearchParams: (data: Partial<any>) => void;
  onOpenContract: (contractId: string) => void;
  onOpenStudent: (studentId: string) => void;
  onCreateContract: (student: { _id: string; fullName: string }) => void;
}

const SyncDot = (props: { row: ContractRowDTO }) => {
  const { synced, reason, lastSyncedAt } = props.row.smartbill;
  const title = lastSyncedAt ? `${reason} · last sync ${moment(lastSyncedAt).fromNow()}` : reason;

  return (
    <span className="inline-flex items-center" title={title}>
      <span
        aria-label={synced ? 'SmartBill synced' : 'SmartBill not synced'}
        className={mergeClassNames(
          'h-2.5 w-2.5 rounded-full',
          synced ? 'bg-customGreen' : 'bg-customRed'
        )}
      />
    </span>
  );
};

const ContractsTable = (props: ContractsTableProps) => {
  const {
    contracts,
    isLoading,
    pagination,
    sortBy,
    sortOrder,
    setSearchParams,
    onOpenContract,
    onOpenStudent,
    onCreateContract,
  } = props;

  const toast = useToast();
  const [syncingContractId, setSyncingContractId] = useState<string | null>(null);

  const { mutate: syncContract } = useSyncContract({
    onSuccess: () => toast.success('Contract refreshed from SmartBill.'),
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'Could not refresh from SmartBill.'),
    onSettled: () => setSyncingContractId(null),
  });

  const syncHandler = (contractId: string) => {
    setSyncingContractId(contractId);
    syncContract([contractId], undefined);
  };

  const sortHandler = (field: string, order: SortOrder) => {
    setSearchParams({ sortBy: order ? field : null, sortOrder: order || null, page: 1 });
  };

  const sortableHeader = (field: string, label: string) => (
    <Table.HeaderCell
      onSort={(order) => sortHandler(field, order)}
      sortOrder={sortBy === field ? sortOrder : undefined}
    >
      {label}
    </Table.HeaderCell>
  );

  return (
    <Table pagination={pagination} isLoading={isLoading} className="mt-1">
      <Table.Header>
        <Table.Row isHeader>
          <Table.HeaderCell>
            <span title="Green when this contract's invoices are synced with SmartBill">SB</span>
          </Table.HeaderCell>
          {sortableHeader('studentName', 'Student / Parent')}
          {sortableHeader('contractDate', 'Contract')}
          {sortableHeader('totalAmount', 'Total')}
          {sortableHeader('remainingAmount', 'Paid / Remaining')}
          {sortableHeader('nextDueDate', 'Installments')}
          {sortableHeader('overdueDays', 'Overdue')}
          <Table.HeaderCell>Sessions</Table.HeaderCell>
          <Table.HeaderCell>Churn</Table.HeaderCell>
          {sortableHeader('status', 'Status')}
          <Table.HeaderCell>Last contact</Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body hasResults={!!contracts?.length}>
        {contracts?.map((contract) => (
          <Table.Row key={contract._id}>
            <Table.Cell>
              <SyncDot row={contract} />
            </Table.Cell>
            <Table.Cell>
              <div className="flex flex-col">
                <button
                  type="button"
                  className="text-left text-white hover:text-customPrimary hover:underline"
                  onClick={() => onOpenStudent(contract.student._id)}
                >
                  {contract.student.fullName}
                </button>
                <span className="text-xs text-customGrey">
                  {contract.parent.fullName ?? '—'}
                  {contract.strategist ? ` · ${contract.strategist.fullName}` : ''}
                </span>
              </div>
            </Table.Cell>
            <Table.Cell>
              {contract.hasContract ? (
                <div className="flex flex-col">
                  <span>{contract.contractNumber || '—'}</span>
                  <span className="text-xs text-customGrey">
                    {formatDate(contract.contractDate)}
                  </span>
                </div>
              ) : (
                <span className="text-customGrey">—</span>
              )}
            </Table.Cell>
            <Table.Cell>
              {contract.hasContract ? (
                <div className="flex flex-col">
                  <span>{formatMoney(contract.summary.totalAmount, contract.currency)}</span>
                  <span className="text-xs text-customGrey">
                    {contract.summary.sessionsInContract} sessions ·{' '}
                    {formatMoney(contract.summary.pricePerSession, contract.currency)}/session
                  </span>
                </div>
              ) : (
                <span className="text-customGrey">—</span>
              )}
            </Table.Cell>
            <Table.Cell>
              {contract.hasContract ? (
                <div className="flex flex-col">
                  <span className="text-customGreen">
                    {formatMoney(contract.summary.paidAmount, contract.currency)}
                  </span>
                  <span
                    className={mergeClassNames(
                      'text-xs',
                      contract.summary.remainingAmount > 0 ? 'text-customGold' : 'text-customGrey'
                    )}
                  >
                    {formatMoney(contract.summary.remainingAmount, contract.currency)} left
                  </span>
                </div>
              ) : (
                <span className="text-customGrey">—</span>
              )}
            </Table.Cell>
            <Table.Cell>
              {contract.hasContract ? (
                <div className="flex flex-col">
                  <span>
                    {contract.summary.installmentsPaid} / {contract.installments.length} paid
                  </span>
                  <span className="text-xs text-customGrey">
                    {contract.summary.nextDueDate
                      ? `Next ${formatDate(contract.summary.nextDueDate)}`
                      : 'No due installment'}
                  </span>
                </div>
              ) : (
                <span className="text-customGrey">—</span>
              )}
            </Table.Cell>
            <Table.Cell>
              {contract.summary.overdueDays > 0 ? (
                <span className="font-semibold text-customRed">
                  {contract.summary.overdueDays} days
                </span>
              ) : (
                <span className="text-customGrey">—</span>
              )}
            </Table.Cell>
            <Table.Cell>
              <div className="flex flex-col">
                <span>
                  {contract.sessions.done} done / {contract.sessions.inAgora} in Agora
                </span>
                <span
                  className={mergeClassNames(
                    'text-xs',
                    contract.sessions.autoNotes.length ? 'text-customGold' : 'text-customGrey'
                  )}
                >
                  {contract.hasContract
                    ? `${contract.sessions.paidNotDone} paid & not done${
                        contract.sessions.autoNotes.length ? ' · check' : ''
                      }`
                    : 'no contract data'}
                </span>
              </div>
            </Table.Cell>
            <Table.Cell>
              <span className={mergeClassNames('capitalize', churnTone(contract.churn?.level))}>
                {contract.churn ? `${contract.churn.level} (${contract.churn.score})` : '—'}
              </span>
            </Table.Cell>
            <Table.Cell>
              <span
                className={mergeClassNames(
                  'rounded-full px-2 py-0.5 text-xs font-semibold',
                  contractStatusTone(contract.status)
                )}
              >
                {CONTRACT_STATUS_LABELS[contract.status]}
              </span>
            </Table.Cell>
            <Table.Cell>
              {contract.lastContact ? (
                <div className="flex flex-col">
                  <span>{formatDate(contract.lastContact.date)}</span>
                  <span className="text-xs text-customGrey">
                    {CONTRACT_CONTACT_FORM_LABELS[contract.lastContact.form]} ·{' '}
                    {contract.lastContact.by.fullName ?? '—'}
                  </span>
                </div>
              ) : (
                <span className="text-customGrey">—</span>
              )}
            </Table.Cell>
            <Table.Cell>
              {contract.hasContract ? (
                <div className="flex gap-2">
                  <IconButton
                    size="small"
                    variant="ghost"
                    icon={<SettingsIcon />}
                    onClick={() => contract.contractId && onOpenContract(contract.contractId)}
                  />
                  <IconButton
                    size="small"
                    variant="ghost"
                    icon={<RefreshIcon className="h-4 w-4" />}
                    isLoading={syncingContractId === contract.contractId}
                    isDisabled={!!syncingContractId}
                    onClick={() => contract.contractId && syncHandler(contract.contractId)}
                  />
                </div>
              ) : (
                <Button
                  variant="secondary"
                  buttonText="Add contract"
                  onClick={() =>
                    onCreateContract({
                      _id: contract.student._id,
                      fullName: contract.student.fullName,
                    })
                  }
                />
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

export default ContractsTable;
